import React from 'react'
import PropTypes from 'prop-types'
import { Input } from 'semantic-ui-react'
import MaskedInput from 'react-text-mask'
import { StyledFormField } from './FormInput.styled.js'
import { FormFieldError } from '../FormFieldError/FormFieldError.jsx'

/**
 * Point a forwarded ref (callback or object) at a node.
 * @private
 */
const assignRef = (ref, node) => {
  if (typeof ref === 'function') {
    ref(node)
  } else if (ref) {
    ref.current = node
  }
}

/**
 * FormInput View Component
 * Pure presentation component for text input fields
 * Supports input masking via react-text-mask for phone, currency, etc.
 */
export const FormInputView = ({
  label,
  placeholder,
  required,
  disabled,
  type,
  icon,
  iconPosition,
  fluid,
  maxLength,
  error,
  errorMessage,
  value,
  onChange,
  onBlur,
  name,
  inputRef,
  mask,
  maskPlaceholder,
  guide,
  prefix,
  suffix,
  'data-testid': dataTestId,
  'data-automation-id': dataAutomationId,
}) => {
  // Build input element (masked or plain)
  // guide={false} for function-shaped masks (createNumberMask): guided mode emits
  // `_` placeholder chars into the value, which has no meaning for a variable-length
  // number. Array masks (phone/zip) are fixed-length and stay guided unless the
  // caller opts out — a date field stores the masked string itself, so placeholder
  // chars would end up in form state.
  const maskGuide = guide ?? (typeof mask === 'function' ? false : undefined)
  const inputElement = mask ? (
    <MaskedInput
      key='input'
      mask={mask}
      guide={maskGuide}
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      // The caller's own placeholder wins; `maskPlaceholder` is the mask's
      // default hint for a field that supplies none (masks.js names it
      // `defaultPlaceholder`). The other way round, a field could not label
      // itself once it had a mask — the currency hint overwrote text as
      // specific as 'Carrier Price (locked)' — and identical props produced a
      // different placeholder depending only on whether a mask was present,
      // since the unmasked branch below has always used `placeholder`.
      placeholder={placeholder || maskPlaceholder}
      maxLength={maxLength}
      render={(setMaskRef, inputProps) => (
        <input
          {...inputProps}
          ref={(node) => {
            setMaskRef(node)
            assignRef(inputRef, node)
          }}
        />
      )}
    />
  ) : (
    // The ref goes on the raw <input>, never on the Semantic <Input> below:
    // Semantic's `focus()` dereferences an internal ref it only holds while
    // this child is mounted, so handing RHF the component instead made
    // RHF's focus-the-first-invalid-field pass throw on a field that had
    // since unmounted (it keeps the stale ref, and a resolver — unlike
    // built-in rules — still produces an error for an unmounted name). The
    // throw aborted handleSubmit before it published state, which left
    // isSubmitting stuck true and the errors never rendered at all.
    // Semantic forwards a child ref (handleChildOverrides -> handleRef) and
    // still fills its own internal ref, so Input.focus() keeps working.
    <input key='input' type={type} maxLength={maxLength} ref={inputRef} />
  )

  // Build children array with prefix/suffix (supports both simultaneously)
  const children = [
    prefix && <span key='prefix'>{prefix}</span>,
    inputElement,
    suffix && <span key='suffix'>{suffix}</span>,
  ].filter(Boolean)

  return (
    <StyledFormField
      error={error || undefined}
      required={required || undefined}
      disabled={disabled || undefined}
    >
      <FormFieldError error={errorMessage} show={error} />
      {label && <label>{label}</label>}

      {/* Use standalone Input component for proper ref forwarding to focusable element */}
      <Input
        name={name}
        value={
          mask
            ? typeof value === 'number'
              ? value.toString()
              : value || ''
            : value
        }
        onChange={mask ? undefined : onChange}
        onBlur={mask ? undefined : onBlur}
        placeholder={mask ? undefined : placeholder}
        disabled={disabled}
        icon={icon}
        iconPosition={iconPosition}
        fluid={fluid}
        // No ref here by design — both branches above hand RHF the real DOM
        // node (masked via MaskedInput's render prop, plain via the child's
        // own ref). See the comment on the unmasked branch.
        data-testid={dataTestId}
        data-automation-id={dataAutomationId}
      >
        {children}
      </Input>
    </StyledFormField>
  )
}

FormInputView.propTypes = {
  label: PropTypes.node,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  type: PropTypes.oneOf(['text', 'number', 'email', 'password', 'tel', 'url']),
  icon: PropTypes.string,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  fluid: PropTypes.bool,
  maxLength: PropTypes.number,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  mask: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  maskPlaceholder: PropTypes.string,
  guide: PropTypes.bool,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  'data-testid': PropTypes.string,
  'data-automation-id': PropTypes.string,
}

FormInputView.defaultProps = {
  type: 'text',
  fluid: true,
  disabled: false,
  required: false,
}
